import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import * as Yup from 'yup';
import { Form, Formik, FormikValues } from 'formik';

// components
import SSOWrapper from '../../components/auth/SSOWrapper';
import ProviderBase from '../../components/auth/providerBase';
import { SsoLoginLogo } from '../../components/auth/ssoLoginLogo';
import Button from '../../components/buttons/basicButton';
import TextDivider from '../../components/display/text/textDivider';
import AuthError from '../../components/errors/AuthError';
import PasswordField from '../../components/inputs/PasswordField';
import TextInput from '../../components/inputs/textInput';
import NavLink from '../../components/nav/NavLink';
import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';
// constants
import routes from '../../constants/routes';

import useFromLocationState from '../../hooks/helper/useFromLocationState';

import Google from '../../images/google.svg?react';

// hooks
import AUTH from '../../services/auth';
// services
import envConfig from '../../services/env-config';

import BaseCard from './baseCard';

const formSchema = Yup.object({
    username: Yup.string().required('Username is required.'),
    password: Yup.string().required('Password is required.'),
});

export default function LoginCard() {
    const history = useHistory();
    const prevLocation = useFromLocationState();
    const [isLoading, setIsLoading] = useState(false);
    const [LoginError, setLoginError] = useState<string | null>(null);

    const params = new URLSearchParams(window.location.search);

    let redirectUrl =
        params.get('redirect') ??
        params.get('redirect_url') ??
        prevLocation?.pathname ??
        window.location.origin; // chained fallback
    const expiredSession = params.get('expired');

    const handleSubmit = async (values: FormikValues) => {
        setIsLoading(true);

        try {
            await AUTH.signInWithCredentials({
                name: values.username,
                password: values.password,
                callbackUrl: redirectUrl,
            });

            if (redirectUrl.startsWith(window.location.origin)) {
                // Internal route — use history.push
                const path = redirectUrl.replace(window.location.origin, '');
                history.push(path || '/');
            } else {
                // External route — fallback to full page reload
                window.location.href = redirectUrl;
            }
        } catch {
            setIsLoading(false);
        }
    };

    const errorParam = params.get('error');
    useEffect(() => {
        if (errorParam) {
            let message = 'An unexpected error occurred.';

            switch (errorParam) {
                case 'CredentialsSignin':
                    message = 'Invalid username or password.';
                    break;
                case 'RateLimitExceededError':
                    message = 'Too many attempts. Please try again later.';
                    break;
                case 'Configuration':
                    message =
                        'Auth configuration error. Please contact support.';
                    break;
            }

            setLoginError(message);
        }
    }, [errorParam]);
    return (
        <BaseCard
            title={`${
                envConfig.ENVIRONMENT === 'staging'
                    ? '[STAGING]'
                    : envConfig.ENVIRONMENT === 'development'
                    ? '[DEV]'
                    : envConfig.ENVIRONMENT === 'local'
                    ? '[LOCAL]'
                    : ''
            } Welcome to Aampe!`}
            subtitle="People, not Segments"
        >
            <SSOWrapper>
                <ProviderBase
                    logo={
                        <Google
                            style={{
                                margin: 0,
                            }}
                        />
                    }
                    SSOProviderName="google"
                    buttonText="Continue with Google"
                    callbackUrl={redirectUrl}
                />
            </SSOWrapper>

            <SSOWrapper>
                <ProviderBase
                    logo={<SsoLoginLogo />}
                    SSOProviderName="boxyhq-saml"
                    buttonText="Continue with SAML SSO"
                    callbackUrl={redirectUrl}
                    noWhiteBackground
                />
            </SSOWrapper>
            <TextDivider text="or login with" />
            {(LoginError || expiredSession) && (
                <AuthError
                    errorMsg={
                        LoginError
                            ? LoginError
                            : 'Your session has expired. Please login again.'
                    }
                />
            )}
            <FormContainer>
                <Formik
                    initialValues={{
                        username: '',
                        password: '',
                    }}
                    validationSchema={formSchema}
                    onSubmit={handleSubmit}
                >
                    {({
                        values,
                        handleChange,
                        handleBlur,
                        errors,
                        touched,
                    }) => (
                        <Form>
                            {expiredSession && (
                                <AuthError errorMsg="Your session has expired. Please login again." />
                            )}
                            <TextInput
                                label="Username:"
                                name={'username'}
                                placeholder="Username"
                                value={values?.username}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors?.username}
                                touched={touched?.username}
                                inputStyle={{
                                    width: '100%',
                                }}
                                wrapperStyle={{
                                    marginBottom: aampeTheme.spacing(1),
                                }}
                            />
                            <PasswordField
                                label="Password:"
                                name={'password'}
                                value={values?.password}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                error={errors?.password}
                                touched={touched?.password}
                                showValidationCheckList={false}
                            />
                            <NavLink
                                destination={routes.forgotPassword}
                                text="Forgot password?"
                            />
                            <StyledButton
                                name="Login"
                                type="submit"
                                isPrimary
                                isDisabled={isLoading}
                                isLoading={isLoading}
                            />
                        </Form>
                    )}
                </Formik>
            </FormContainer>
        </BaseCard>
    );
}

const FormContainer = styled.div({
    width: '100%',
});

const StyledButton = styled(Button)`
    width: 100%;
    margin: ${aampeTheme.spacing(2, 0)};
    font-size: 16px;
    font-weight: 600;
`;
